import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { IconButton, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageContent } from "../components/PageContent";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../components/Table";
import { CatalogListName } from "../components/CatalogOriginChip";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { useShowCatalogOriginMarkers } from "../hooks/useShowCatalogOriginMarkers";
import { CATALOG_NEW_ITEM_ID } from "./catalog/catalogNav";
import {
  useGetModifierGroupsQuery,
  useDeleteModifierGroupMutation,
} from "../query/modifierGroupsQuery";
import type { ModifierGroupId } from "@KassaroAB/shared/types/ids";

export const ModifierGroupsView = () => {
  const { t } = useTranslation();
  const { scope, pagePath } = useCatalogScope("modifierGroups");
  const showCatalogOrigin = useShowCatalogOriginMarkers(scope);

  const [deleteId, setDeleteId] = useState<ModifierGroupId | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    data: groups,
    isLoading,
    error: loadError,
  } = useGetModifierGroupsQuery(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteModifierGroupMutation(scope);

  const groupList = groups ?? [];
  const deleteName =
    groupList.find((group) => group.id === deleteId)?.name ?? "";

  const confirmDelete = () => {
    if (!deleteId) return;
    deleteMutation(deleteId, {
      onSuccess: () => {
        setSuccessMessage(t("modifierGroupsView.successDeleted"));
        setDeleteId(null);
      },
      onError: () => setSuccessMessage(null),
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  if (isLoading && groupList.length === 0) {
    return (
      <PageContent title={t("modifierGroupsView.title")}>
        <LoadingContainer message={t("modifierGroupsView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("modifierGroupsView.title")}
      actions={
        <RouterButtonLink to={`${pagePath}/${CATALOG_NEW_ITEM_ID}`}>
          <Plus size={16} aria-hidden />
          {t("modifierGroupsView.create")}
        </RouterButtonLink>
      }
    >
      {loadError && (
        <InfoBox variant="error">{t("modifierGroupsView.errorLoad")}</InfoBox>
      )}
      {deleteError && (
        <InfoBox variant="error">{t("modifierGroupsView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      {groupList.length > 0 ? (
        <Card>
          <Table minWidth="640px">
            <TableHeader>
              <tr>
                <TableHeaderCell>
                  {t("modifierGroupsView.tableName")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("modifierGroupsView.tableModifiers")}
                </TableHeaderCell>
                <TableHeaderCell align="right">{null}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {groupList.map((group) => (
                <TableRow key={group.id} to={`${pagePath}/${group.id}`}>
                  <TableCell noWrap>
                    <CatalogListName
                      name={group.name}
                      origin={group.origin}
                      showOrigin={showCatalogOrigin}
                    />
                  </TableCell>
                  <TableCell noWrap>
                    {String(group.modifiers?.length ?? 0)}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      type="button"
                      $color="danger"
                      aria-label={t("common.delete")}
                      disabled={isDeletePending}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setDeleteId(group.id);
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <EmptyState
            message={`${t("modifierGroupsView.empty")} ${t("modifierGroupsView.emptyHint")}`}
          />
        </Card>
      )}
      <ConfirmDialog
        isOpen={deleteId != null}
        title={t("modifierGroupForm.deleteModifierGroup")}
        message={t("modifierGroupsView.deleteConfirm", { name: deleteName })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </PageContent>
  );
};
